import React, { useEffect, useRef, useState } from 'react'
import { LazyImage, Button } from '../../components'
import { IoClose } from 'react-icons/io5'
import { useTheme } from 'styled-components'
import { getUserTheme } from '../../utils/localStorage'
import { useResponsive } from '../../hooks'
import { media } from '../../utils/media'
/**
 * FaceSwapModal - A customizable modal component
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {string} [props.title] - Modal title
 * @param {string} [props.description] - Modal description
 * @param {Array} [props.actions] - Array of action card objects: { icon, title, description, onClick, color }
 * @param {React.ReactNode} [props.children] - Custom content to render instead of actions
 */
const FaceSwapModal = ({
  open,
  onClose,
  title,
  description,
  actions,
  children,
  closeButtonStyle,
  englishText,
  hindiText,
  modalStyle,
  modalContainerStyle
}) => {
  const [isClosing, setIsClosing] = useState(false)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const modalRef = useRef(null)
  const startY = useRef(0)
  const currentY = useRef(0)
  const startX = useRef(0)
  const currentX = useRef(0)
  const theme = useTheme()
  const isMobile = useResponsive(media.mobile)
  const isNotSent = true

  useEffect(() => {
    if (!open) {
      setIsClosing(false)
      setDragY(0)
    }
  }, [open])

  const handleClose = (fromDrag = false) => {
    setIsClosing(true)
    if (!fromDrag) {
      setDragY(0)
    }
    setTimeout(() => {
      onClose()
    }, 300)
  }

  const handleDragStart = (e) => {
    setIsDragging(true)
    if (isMobile) {
      startY.current = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY
      currentY.current = startY.current
    } else {
      startX.current = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX
      currentX.current = startX.current
    }
  }

  const handleDragMove = (e) => {
    if (!isDragging) return
    e.preventDefault()

    if (isMobile) {
      // Vertical dragging for mobile (bottom to top)
      const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY
      const deltaY = clientY - startY.current
      if (deltaY > 0) {
        setDragY(deltaY)
        currentY.current = clientY
      }
    } else {
      // Horizontal dragging for desktop (right to left)
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX
      const deltaX = startX.current - clientX // Invert for right to left
      if (deltaX > 0) {
        setDragY(deltaX) // Reusing dragY for horizontal movement
        currentX.current = clientX
      }
    }
  }

  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    const threshold = 100
    if (dragY > threshold) {
      handleClose(true)
    } else {
      setDragY(0)
    }
  }

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e)
      const handleMouseUp = () => handleDragEnd()
      const handleTouchMove = (e) => handleDragMove(e)
      const handleTouchEnd = () => handleDragEnd()

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false
      })
      document.addEventListener('touchend', handleTouchEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isDragging, dragY])

  if (!open) return null

  return (
    <div
      className={`fixed inset-0 z-[1000] flex transition-opacity duration-300 ${
        isClosing ? 'bg-opacity-0' : 'bg-opacity-50'
      } ${isMobile ? 'items-end justify-center' : 'items-center justify-end'}`}
      onClick={handleClose}
      style={{
        ...modalStyle
      }}
    >
      <div
        ref={modalRef}
        className={`relative shadow-lg flex flex-col ${
          isMobile
            ? 'w-[96vw] max-h-[80vh] max-w-md mx-auto  rounded-t-2xl p-6 pb-8'
            : 'w-[400px] h-full p-6 border-l-2'
        }  ${
          isClosing
            ? ''
            : isMobile
              ? 'animate-slideInUp'
              : 'animate-slideInRight'
        }`}
        style={{
          background: theme?.app?.background,
          border : '1px solid #666666',
          transform: isClosing
            ? isMobile
              ? `translateY(calc(100% + ${dragY}px))`
              : `translateX(calc(100% + ${dragY}px))`
            : isMobile
              ? `translateY(${dragY}px)`
              : `translateX(${dragY}px)`,
          transition: isDragging
            ? 'none'
            : 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          ...modalContainerStyle
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        {isMobile && (
          <div
            className={`flex cursor-grab active:cursor-grabbing ${
              isMobile ? 'justify-center mb-4' : 'justify-start mb-4'
            }`}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <div
              className={`bg-gray-700 rounded-full ${
                isMobile ? 'w-10 h-1' : 'w-1 h-10'
              }`}
            />
          </div>
        )}
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl"
          style={{
            top: closeButtonStyle?.top || '16px',
            ...closeButtonStyle
          }}
          onClick={handleClose}
          aria-label="Close"
        >
          <IoClose />
        </button>
        {/* Title & Description */}
        {(title || description) && (
          <div className="my-4">
            {title && (
              <h2
                className={` text-2xl mb-4 text-start`}
                style={{ color: theme.app.text }}
              >
                {title}
              </h2>
            )}
            {description && (
              <p
                className={` text-sm text-start`}
                style={{ color: theme.app.text }}
              >
                {description}
              </p>
            )}

            {isMobile && englishText && hindiText && (
              <div
                className={`
    flex
    ${isMobile ? 'flex-row' : 'flex-row-reverse'}
    gap-2
    mt-8
  `}
              >
                <Button
                  theme={theme}
                  background={'#00B8F8'}
                  border={
                    isMobile
                      ? 'transparent'
                      : isNotSent
                        ? '1px solid #00B8F8'
                        : 'transparent'
                  }
                  padding="12px 24px"
                  style={{
                    color: theme.app.text,
                    width: '100%'
                  }}
                  onClick={() => {}}
                >
                  <svg
                    className="inline-block w-4 h-4 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {englishText}
                </Button>

                <Button
                  theme={theme}
                  padding="10px 22px"
                  background={'#00B8F8'}
                  style={{
                    color: theme.app.text,
                    width: '100%'
                  }}
                  onClick={() => {}}
                >
                  <svg
                    className="inline-block w-4 h-4 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {hindiText}
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Action Cards or Custom Content */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {actions && actions.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {actions.map((action, idx) => (
                <div
                  key={idx}
                  className="flex-1 h-[120px] w-full border rounded-xl flex flex-col items-start relative"
                  style={{
                    borderColor: action.color || '#00B8F8'
                  }}
                  onClick={action.onClick}
                >
                  {action.image && (
                    <>
                      <LazyImage
                        src={action.image}
                        alt={action.title}
                        className="w-full h-full object-cover rounded-xl"
                      />
                      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/80 to-transparent rounded-b-xl pointer-events-none z-10" />
                    </>
                  )}
                  <div className="px-2 rounded-lg absolute top-3">
                    {action.icon}
                  </div>
                  <div className="absolute bottom-1 z-10">
                    <div
                      className={`font-semibold text-white text-start px-2 text-base`}
                    >
                      {action.title}
                    </div>
                    <div className="text-xs text-start mt-auto px-2">
                      {action.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            children
          )}
        </div>
      </div>
      {/* Animation keyframes */}
      <style>{`
        .animate-slideInUp {
          animation: slideInUp 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes slideInUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  )
}

export default FaceSwapModal
